import {
  VStack, HStack, Text, Heading, Button, Box, Divider,
} from '@chakra-ui/react';

export interface WizardPreviewData {
  location?: { name: string; type: string; address?: string };
  music?: { genres: string[]; moods: string[]; tempo: string };
  musicPreferences?: { genres: string[]; moods: string[] };
  organization?: { name: string };
  scenario?: { name: string; description?: string };
  subscription?: { planId: string };
}

export interface PreviewStepProps {
  previewData?: WizardPreviewData | null;
  locationData?: { name: string; venueType: string; address?: string };
  musicData?: { genres: string[]; moods: string[]; tempo: string };
  scenarioData?: { name: string; description: string };
  onConfirm: () => void;
  onEdit: (step: string) => void;
  isLoading?: boolean;
  error?: string | null;
}

export function PreviewStep({
  previewData,
  locationData,
  musicData,
  scenarioData,
  onConfirm,
  onEdit,
  isLoading,
}: PreviewStepProps) {
  // Use previewData as fallback for individual data props
  const location = previewData?.location
    ? { name: previewData.location.name, venueType: previewData.location.type, address: previewData.location.address }
    : locationData;
  const music = musicData || previewData?.music;
  const scenario = previewData?.scenario
    ? { name: previewData.scenario.name, description: previewData.scenario.description || '' }
    : scenarioData;
  return (
    <VStack
      flex={1}
      p={6}
      gap={6}
      bg="gray.50"
      overflowY="auto"
      data-testid="wizard-preview-step"
    >
      <VStack align="center" gap={2}>
        <Heading as="h2" size="xl" fontWeight="bold" color="gray.800">
          Review your setup
        </Heading>
        <Text fontSize="md" color="gray.500" textAlign="center">
          Make sure everything looks right before completing
        </Text>
      </VStack>

      <VStack gap={4} w="100%" maxW="500px" alignSelf="center">
        {/* Location Card */}
        {location && (
          <Box p={4} bg="white" borderRadius="md" borderWidth="1px" borderColor="gray.200" w="100%">
            <HStack justify="space-between" align="center" mb={2}>
              <Text fontSize="sm" fontWeight="600" color="gray.500" textTransform="uppercase">
                Location
              </Text>
              <Button
                size="xs"
                variant="ghost"
                color="brand.500"
                onClick={() => onEdit('location')}
              >
                Edit
              </Button>
            </HStack>
            <Text fontWeight="600" color="gray.800">{location.name}</Text>
            <Text fontSize="sm" color="gray.500">{location.venueType}</Text>
            {location.address && (
              <Text fontSize="sm" color="gray.500">{location.address}</Text>
            )}
          </Box>
        )}

        {/* Music Card */}
        {music && (
          <Box p={4} bg="white" borderRadius="md" borderWidth="1px" borderColor="gray.200" w="100%">
            <HStack justify="space-between" align="center" mb={2}>
              <Text fontSize="sm" fontWeight="600" color="gray.500" textTransform="uppercase">
                Music Preferences
              </Text>
              <Button
                size="xs"
                variant="ghost"
                color="brand.500"
                onClick={() => onEdit('music')}
              >
                Edit
              </Button>
            </HStack>
            <VStack gap={2} align="stretch">
              <Box>
                <Text fontSize="xs" color="gray.500">Genres</Text>
                <Text fontSize="sm" color="gray.800">{music.genres.join(', ')}</Text>
              </Box>
              <Box>
                <Text fontSize="xs" color="gray.500">Moods</Text>
                <Text fontSize="sm" color="gray.800">{music.moods.join(', ')}</Text>
              </Box>
              <Box>
                <Text fontSize="xs" color="gray.500">Tempo</Text>
                <Text fontSize="sm" color="gray.800">{music.tempo}</Text>
              </Box>
            </VStack>
          </Box>
        )}

        {/* Scenario Card */}
        {scenario && (
          <Box p={4} bg="white" borderRadius="md" borderWidth="1px" borderColor="gray.200" w="100%">
            <HStack justify="space-between" align="center" mb={2}>
              <Text fontSize="sm" fontWeight="600" color="gray.500" textTransform="uppercase">
                Scenario
              </Text>
              <Button
                size="xs"
                variant="ghost"
                color="brand.500"
                onClick={() => onEdit('scenario')}
              >
                Edit
              </Button>
            </HStack>
            <Text fontWeight="600" color="gray.800">{scenario.name}</Text>
            {scenario.description && (
              <Text fontSize="sm" color="gray.500">{scenario.description}</Text>
            )}
          </Box>
        )}

        <Divider my={2} />

        <Button
          size="lg"
          bg="brand.500"
          color="white"
          fontWeight="bold"
          w="100%"
          onClick={onConfirm}
          isDisabled={isLoading}
          data-testid="wizard-preview-confirm"
          _hover={{ bg: 'brand.600' }}
        >
          {isLoading ? 'Setting up...' : 'Complete Setup'}
        </Button>
      </VStack>
    </VStack>
  );
}

export default PreviewStep;
